import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShoppingCart, X, Trash2, ArrowRight } from "lucide-react";
import { useBasket } from "@/lib/basket";
import { scrollToId, QUOTE_EVENT } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function QuoteBasket() {
    const { items, remove, clear } = useBasket();
    const [open, setOpen] = useState(false);

    const submit = () => {
        const message = "Quote request for:\n" + items.map((i) => `• ${i.sku} — ${i.name} (Qty: ${i.qty || 1})`).join("\n");
        window.dispatchEvent(new CustomEvent(QUOTE_EVENT, { detail: { interest: "General Enquiry", message } }));
        setOpen(false);
        scrollToId("enquiry");
        toast.success("Quote list added to the enquiry form", {
            description: `${items.length} item${items.length > 1 ? "s" : ""} ready to send`,
        });
    };

    return (
        <>
            <AnimatePresence>
                {items.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 40 }}
                        transition={{ duration: 0.4 }}
                        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40"
                        data-testid="quote-basket-bar"
                    >
                        <button
                            data-testid="quote-basket-drawer-toggle"
                            onClick={() => setOpen(true)}
                            className="flex items-center gap-3 bg-inkdark text-white rounded-full pl-5 pr-2 py-2 shadow-[0_16px_40px_rgba(20,21,24,0.35)] hover:scale-[1.02] transition-transform duration-300"
                        >
                            <ShoppingCart size={16} className="text-copper-bright" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Quote Basket</span>
                            <span
                                data-testid="quote-basket-count"
                                className="w-7 h-7 rounded-full bg-copper-bright text-white font-mono text-xs flex items-center justify-center"
                            >
                                {items.length}
                            </span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            className="fixed inset-0 bg-inkdark/50 z-40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                        />
                        <motion.aside
                            data-testid="quote-basket-drawer"
                            className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white z-50 border-l border-line shadow-2xl flex flex-col"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
                                <p className="font-display font-semibold text-xl text-ink">
                                    Quote Basket <span className="text-copper">({items.length})</span>
                                </p>
                                <button
                                    data-testid="quote-basket-close-btn"
                                    onClick={() => setOpen(false)}
                                    className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-mist hover:text-copper hover:border-copper transition-colors duration-300"
                                    aria-label="Close basket"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto divide-y divide-line">
                                {items.length === 0 ? (
                                    <p className="p-8 text-sm text-mist text-center">
                                        Basket is empty — add SKUs from the catalogue.
                                    </p>
                                ) : (
                                    items.map((i) => (
                                        <div key={i.id} className="p-5 flex items-start justify-between gap-4" data-testid={`quote-basket-item-row-${slug(i.sku)}`}>
                                            <div>
                                                <p className="font-mono text-[10px] tracking-[0.2em] text-copper uppercase mb-1">
                                                    {i.sku} · {i.category}
                                                </p>
                                                <p className="font-display font-semibold text-base text-ink leading-snug">{i.name}</p>
                                                <p className="font-mono text-[10px] text-dim mt-1">
                                                    {i.voltage} · {i.cores} · {i.sizes.slice(0, 4).join(" / ")} · Qty: {i.qty || 1}
                                                </p>
                                            </div>
                                            <button
                                                data-testid={`quote-basket-remove-${slug(i.sku)}`}
                                                onClick={() => remove(i.id)}
                                                className="text-dim hover:text-red-600 transition-colors duration-300 mt-1"
                                                aria-label={`Remove ${i.name}`}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {items.length > 0 && (
                                <div className="p-5 border-t border-line space-y-3">
                                    <button
                                        data-testid="quote-basket-submit-btn"
                                        onClick={submit}
                                        className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper-bright transition-colors duration-300 flex items-center justify-center gap-2"
                                    >
                                        Send as Enquiry <ArrowRight size={14} />
                                    </button>
                                    <button
                                        data-testid="quote-basket-clear-btn"
                                        onClick={clear}
                                        className="w-full font-mono text-[10px] uppercase tracking-[0.2em] text-dim hover:text-red-600 transition-colors duration-300 py-1"
                                    >
                                        Clear all
                                    </button>
                                </div>
                            )}
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
